import type { LoaderFunctionArgs, LinksFunction } from '@remix-run/node'
import type { ShouldRevalidateFunction } from '@remix-run/react'
import {
  Links,
  LiveReload,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useLocation,
  useNavigation,
} from '@remix-run/react'
import { json } from '@remix-run/server-runtime'
import { withSentry } from '@sentry/remix'
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { GeneralErrorBoundary } from '~/components/error-boundary'
import { ErrorPage } from '~/components/ErrorPage'
import { FileUploadProvider } from '~/contexts/FileUploadContext'

import style from './globals.css?url'
import { Button } from './ui'

declare global {
  interface Window {
    ENV: {
      NODE_ENV: string
      IS_STAGING: string
      APP_URL: string
      IMAGEKIT_ENDPOINT: string
      IMAGEKIT_PUBLIC_KEY: string
      SENTRY_DSN: string
      SUPPORT_EMAIL: string
    }
  }
}

export const links: LinksFunction = () => {
  return [{ rel: 'stylesheet', href: style }]
}

export const loader = async ({ request }: LoaderFunctionArgs) => {
  const ENV = {
    NODE_ENV: process.env.NODE_ENV,
    IS_STAGING: process.env.IS_STAGING,
    APP_URL: process.env.APP_URL,
    IMAGEKIT_ENDPOINT: process.env.IMAGEKIT_ENDPOINT,
    IMAGEKIT_PUBLIC_KEY: process.env.IMAGEKIT_PUBLIC_KEY,
    SENTRY_DSN: process.env.SENTRY_DSN,
    SUPPORT_EMAIL: process.env.SUPPORT_EMAIL,
  }

  return json({
    ENV,
  })
}

function App() {
  const data = useLoaderData<typeof loader>()

  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta
          name="viewport"
          content="width=device-width,initial-scale=1,maximum-scale=1"
        />
        <meta name="theme-color" content="#101c18" />
        <Meta />
        <Links />
      </head>
      <body>
        <FileUploadProvider>
          <Outlet />
        </FileUploadProvider>
        <Scripts />
        <ContainerScrollReset containerId="shop-container" />
        <ScrollRestoration />
        <LiveReload />
        <script
          dangerouslySetInnerHTML={{
            __html: `window.ENV = ${JSON.stringify(data.ENV)}`,
          }}
        />
      </body>
    </html>
  )
}

export default withSentry(App)

export function ErrorBoundary() {
  const { t } = useTranslation()

  const errorStatusHandlers = {
    404: () => (
      <ErrorPage
        title={t('page.not_found.title')}
        description={t('page.not_found.description')}
      >
        <Button size="lg" asChild>
          <a href="https://protocol.store/">{t('action.go_to_homepage')}</a>
        </Button>
      </ErrorPage>
    ),
    500: () => (
      <ErrorPage
        title={t('errors.there_was_an_error')}
        description={t('errors.page_temporarily_unavailable')}
      >
        <Button size="lg" onClick={() => window.location.reload()}>
          {t('action.try_again')}
        </Button>
        <Button
          size="lg"
          variant="outline"
          onClick={() => window.history.back()}
        >
          {t('action.go_back')}
        </Button>
      </ErrorPage>
    ),
  }

  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta
          name="viewport"
          content="width=device-width,initial-scale=1,maximum-scale=1"
        />
        <Links />
      </head>
      <body>
        <GeneralErrorBoundary statusHandlers={errorStatusHandlers} />
        <ScrollRestoration />
        <Scripts />
        <LiveReload />
      </body>
    </html>
  )
}

export const shouldRevalidate: ShouldRevalidateFunction = ({
  currentParams,
  nextParams,
  formMethod,
  defaultShouldRevalidate,
}) => {
  // Revalidate if URL params change significantly
  if (currentParams.shopSlug !== nextParams.shopSlug) {
    return true
  }

  // Revalidate after form submissions
  if (formMethod && formMethod !== 'GET') {
    return true
  }

  return defaultShouldRevalidate
}

/**
 * Scrolls #shop-container to top on every pathname change, unless the
 * navigation explicitly opts out via `preventScrollReset` in location state.
 *
 * The products/collections tab switch already passes `preventScrollReset`
 * on its Links, which Remix forwards to location.state — so tab switches
 * preserve scroll while all other navigations reset to top.
 */
const ContainerScrollReset = ({ containerId }: { containerId: string }) => {
  const location = useLocation()
  const navigation = useNavigation()
  const prevPathname = useRef(location.pathname)

  // When a scroll-preserving navigation starts, lock the content height
  // so the browser doesn't clamp scrollTop when content shrinks.
  useEffect(() => {
    const container = document.getElementById(containerId)
    if (!container) return

    const content = container.firstElementChild as HTMLElement | null
    if (!content) return

    if (
      navigation.state === 'loading' &&
      navigation.location?.state?.preventScrollReset
    ) {
      content.style.minHeight = `${content.scrollHeight}px`
    }

    if (navigation.state === 'idle') {
      content.style.minHeight = ''
    }
  }, [navigation.state, navigation.location?.state, containerId])

  useEffect(() => {
    if (location.pathname === prevPathname.current) return
    prevPathname.current = location.pathname

    if (location.state?.preventScrollReset) return

    const container = document.getElementById(containerId)
    if (container) {
      container.scrollTo(0, 0)
    }
  }, [location.pathname, location.state, containerId])

  return null
}
